import React, { useEffect, useState } from "react";
import { CreditCard, Receipt, ExternalLink, Users, Calendar, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";

const PLAN_LABEL = { starter: "Starter", team: "Team", enterprise: "Enterprise" };

export default function Billing() {
  const [data, setData] = useState(null);
  const [porting, setPorting] = useState(false);

  useEffect(() => { api.get("/billing").then((r) => setData(r.data)).catch(() => {}); }, []);

  const openPortal = async () => {
    setPorting(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing portal is not available yet.");
      setPorting(false);
    }
  };

  if (!data) return <DashboardLayout title="Billing"><p className="text-[#8B9BB4]">Loading…</p></DashboardLayout>;

  const planName = PLAN_LABEL[data.plan] || data.plan;
  const txns = data.transactions || [];
  const interval = data.plan_interval === "year" ? "Billed yearly" : data.plan_interval === "month" ? "Billed monthly" : "";

  return (
    <DashboardLayout title="Billing">
      <div className="fade-up space-y-6 max-w-4xl">
        {/* Current plan */}
        <div className="iw-card p-8" data-testid="billing-plan-card">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <p className="overline">Current plan</p>
              <div className="flex items-end gap-3 mt-2">
                <h2 className="font-display text-4xl font-bold text-[#D4AF37]" data-testid="billing-plan-name">{planName}</h2>
                {interval && <span className="text-sm text-[#8B9BB4] mb-1.5">{interval}</span>}
              </div>
              <div className="flex flex-wrap gap-6 mt-5">
                <div className="flex items-center gap-2 text-sm text-[#8B9BB4]">
                  <Users size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
                  <span data-testid="billing-seats">{data.plan_seats} seat{data.plan_seats === 1 ? "" : "s"}</span>
                </div>
                {data.plan_since && (
                  <div className="flex items-center gap-2 text-sm text-[#8B9BB4]">
                    <Calendar size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
                    <span>Since {new Date(data.plan_since).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {data.has_customer ? (
                <button onClick={openPortal} disabled={porting} data-testid="manage-billing-btn"
                  className="btn-gold px-6 py-3 rounded-full flex items-center gap-2 disabled:opacity-60">
                  <CreditCard size={18} strokeWidth={1.5} /> {porting ? "Opening…" : "Manage Billing"}
                  {!porting && <ExternalLink size={14} />}
                </button>
              ) : (
                <Link to="/pricing" data-testid="upgrade-billing-btn"
                  className="btn-gold px-6 py-3 rounded-full flex items-center gap-2">
                  <ArrowUpRight size={18} strokeWidth={1.5} /> Upgrade plan
                </Link>
              )}
              {data.has_customer && (
                <Link to="/pricing" className="btn-ghost px-6 py-3 rounded-full text-center text-sm" data-testid="change-plan-link">
                  Change plan
                </Link>
              )}
            </div>
          </div>
          {data.has_customer && (
            <p className="text-xs text-[#8B9BB4] mt-6 border-t border-[#ffffff1a] pt-4">
              Update payment methods, download invoices, or cancel your subscription anytime through the secure Stripe portal.
            </p>
          )}
        </div>

        {/* Transactions */}
        <div className="iw-card p-6" data-testid="billing-history">
          <p className="overline flex items-center gap-2 mb-4"><Receipt size={14} /> Payment history</p>
          {data.transactions.length === 0 ? (
            <p className="text-sm text-[#8B9BB4] py-6 text-center">No transactions yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#8B9BB4] border-b border-[#ffffff1a]">
                    <th className="py-2.5 font-medium">Date</th>
                    <th className="py-2.5 font-medium">Plan</th>
                    <th className="py-2.5 font-medium">Amount</th>
                    <th className="py-2.5 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-[#ffffff1a]/50" data-testid={`txn-${tx.id}`}>
                      <td className="py-3 text-[#8B9BB4]">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : "—"}</td>
                      <td className="py-3 text-[#F6F1E4]">{(tx.lookup_key || "").replace(/_/g, " ")}</td>
                      <td className="py-3 text-[#F6F1E4]">{tx.amount != null ? `$${tx.amount} ${(tx.currency || "").toUpperCase()}` : "—"}</td>
                      <td className="py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          tx.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-[#D4AF37]/15 text-[#D4AF37]"
                        }`}>{tx.payment_status || "pending"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
